/**
 * PageBuilderSchema - Single Source of Truth
 * 
 * AI reads this file to understand all section types, props constraints,
 * overlay types, and style classes available in the page builder.
 */

import { SECTION_TYPES } from './section-types';
import { OVERLAY_TYPES } from './overlay-types';
import { STYLE_CLASSES } from './style-classes';
import { TEXT_COLORS } from './text-colors';
import type { PageBuilderSchema } from './types';

export const PAGE_BUILDER_SCHEMA: PageBuilderSchema = {
  version: '1.0',
  sectionTypes: SECTION_TYPES,
  overlayTypes: OVERLAY_TYPES,
  styleClasses: STYLE_CLASSES,
  textColors: TEXT_COLORS,
};

// Re-export everything
export * from './types';
export { SECTION_TYPES } from './section-types';
export { OVERLAY_TYPES } from './overlay-types';
export { STYLE_CLASSES, getStyleClassesForType, getStyleClassById } from './style-classes';
export {
  ALLOWED_STYLE_PROPERTIES,
  CSS_PROPERTY_RTL_MAP,
  validateStyleClass,
  getStyleClassOptionsForType,
  type StyleClassValidationResult,
  type StyleClassOption,
} from './style-class-validation';
export {
  TEXT_COLORS,
  getTextColorById,
  getTextColorOptions,
  isValidTextColor,
  resolveTextColor,
  type TextColorDefinition,
} from './text-colors';
export {
  validatePageConfigAgainstSchema,
  isSectionTypeValid,
  getAvailableSectionTypes,
  type ValidationResult,
  type ValidationError,
} from './validation';
export {
  getBuilderSectionTypeOptions,
  getDefaultPropsForSectionType,
  getSectionTypeMeta,
  getSlotEditingConfig,
  type BuilderSectionTypeOption,
} from './builder';

// Appearance token registries (used by builder UI components like AppearanceControls)
export {
  BACKGROUND_COLORS,
  getBackgroundColorById,
  getBackgroundColorOptions,
  isValidBackgroundColor,
  resolveBackgroundColor,
  type BackgroundColorDefinition,
} from './background-colors';
export {
  RADII,
  getRadiusById,
  getRadiusOptions,
  isValidRadius,
  resolveRadius,
  type RadiusDefinition,
} from './radii';
export {
  SHADOWS,
  getShadowById,
  getShadowOptions,
  isValidShadow,
  resolveShadow,
  type ShadowDefinition,
} from './shadows';
export {
  SPACING,
  getSpacingById,
  getSpacingOptions,
  isValidSpacing,
  resolveSpacing,
  type SpacingDefinition,
} from './spacing';

// Widget definition system (Page Customizer)
export {
  defineWidget,
  WIDGET_CATEGORIES,
  type WidgetDefinition,
  type WidgetCatalogItem,
  type WidgetCategory,
} from './widget-types';
export { WIDGET_REGISTRY, toSectionTypes } from './widget-definitions';

// Block utilities (centralized grouping, hydration, sorting)
export {
  hydrateGroupIds,
  sortByOrder,
  groupBlocksByGroupId,
} from './block-utils';

// Block content config (pure data — social icons, contact icons, href validation)
export {
  isSafeHref,
  SOCIAL_PLATFORM_CONFIG,
  CONTACT_ICON_CONFIG,
  type SocialPlatformEntry,
  type ContactIconEntry,
} from './block-content-config';